import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'
import Login from './screens/Login'
import Main from './screens/Main'
import AcceptInvite from './screens/AcceptInvite'
import PublicSquad from './screens/PublicSquad'
import PublicClub from './screens/PublicClub'
import PublicMatchReport from './screens/PublicMatchReport'
import Landing from './screens/Landing'
import Help from './screens/Help'
import Contact from './screens/Contact'
import Privacy from './screens/Privacy'
import Terms from './screens/Terms'
import SiteAdmin from './screens/SiteAdmin'

export default function App() {
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [isInviteFlow, setIsInviteFlow] = useState(false)

  useEffect(() => {
  const hash = window.location.hash
    const params = new URLSearchParams(hash.replace('#', '?'))
    const type = params.get('type')
    // Old Supabase-invite screen switched off: it built profiles from user-editable metadata.
    if (type === 'recovery') {
      // Exchange the token for a session so updateUser works
      const accessToken = params.get('access_token')
      const refreshToken = params.get('refresh_token')
      if (accessToken) {
        supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken || '' })
          .then(() => {
            window.history.replaceState({}, '', '/?reset=true')
            setLoading(false)
          })
      }
      return
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      if (session) loadProfile(session.user.id)
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (isInviteFlow) return
      setSession(prevSession => {
        // Only reload profile data if this is genuinely a different user
        // than we already have loaded — Supabase sometimes fires SIGNED_IN
        // on routine background token refresh, not just real logins.
        const sameUser = prevSession?.user?.id && session?.user?.id && prevSession.user.id === session.user.id
        if (event === 'SIGNED_OUT' || !session) {
          setProfile(null)
          setLoading(false)
        } else if (!sameUser) {
          loadProfile(session.user.id)
        }
        return session
      })
    })

    return () => subscription.unsubscribe()
  }, [])

  async function loadProfile(userId) {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('profiles').select('*').eq('id', userId).maybeSingle()

      if (error) { console.error('Profile error:', error); setLoading(false); return }

      if (data) {
        // Load club info
        if (data.club_id) {
          const { data: club } = await supabase
            .from('clubs').select('*').eq('id', data.club_id).maybeSingle()
          data.clubs = club || {}
        }

        // Load squad memberships
        const { data: memberships } = await supabase
          .from('squad_members')
          .select('*, squads(id, name, sport, age_group, gender)')
          .eq('profile_id', userId)
          .eq('approved', true)
        data.squadMemberships = memberships || []
        data.squadIds = (memberships || []).map(m => m.squad_id)

        setProfile(data)
        // Don't sign out unapproved users — show pending screen instead
      }
    } catch (e) {
      console.error('Profile load error:', e)
    }
    setLoading(false)
  }

  function handleInviteComplete() {
    setIsInviteFlow(false)
    window.location.hash = ''
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      if (session) loadProfile(session.user.id)
    })
  }

  // Public club page — must come before squad check
  if (window.location.pathname.startsWith('/public/club/')) {
    return <PublicClub />
  }
  if (window.location.pathname.includes('/report/')) {
    return <PublicMatchReport />
  }

  // Public squad page — no auth required
  if (window.location.pathname.startsWith('/public/')) {
    return <PublicSquad />
  }

  // Contact us page — no auth required
  if (window.location.pathname === '/help') {
    return <Help />
  }
  if (window.location.pathname === '/contact') {
    return <Contact />
  }

  if (window.location.pathname === '/privacy') {
    return <Privacy />
  }
  if (window.location.pathname === '/terms') {
    return <Terms />
  }

  // Marketing landing page at root — no auth required
  if (window.location.pathname === '/' && !session && !loading) {
  return <Landing />
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#185FA5' }}>
        <div style={{ color: 'white', fontSize: '16px' }}>Loading...</div>
      </div>
    )
  }

  if (window.location.pathname === '/site-admin') {
    return session ? <SiteAdmin profile={profile} /> : <Login />
  }

  if (isInviteFlow) return <AcceptInvite onComplete={handleInviteComplete} />
  if (!session) return <Login />
  return <Main session={session} profile={profile} onProfileUpdate={setProfile} />
}